import { clone, cloneDeep, get, padStart } from 'lodash-es'
import axios from 'axios'
import { findParentPath } from '../../utils'
import MbTextBox from '../textBox'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, merchConstants } from '@/models/constants'
import utils from '@/services/utils'
import type MyArticle from '@/models/myArticle'
import type Merch from '@/modules/merch/services/merch'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import appConfig from '@/services/appConfig'
import { AttributeType } from '@/models/catalogAttribute'
import type ArticleSegmentation from '@/models/articleSegmentation'
import visualLineBuilderImage from '@/assets/templateLayoutImages/visualLineBuilder.png'

class VisualLineBuilder implements IMerchTemplate {
  id = merchConstants.slideGenTemplatesId.visualLineBuilder
  name = 'Visual line Builder'
  layoutImage = visualLineBuilderImage
  slideWidth: number = 1280
  slideHeight: number = 720
  headerHeight: number = 30
  font: string = 'Arial'
  labelHeightPerLine: number = 20
  headerProp = {
    fontSize: 20,
    fontWeight: 'bold',
    fontStyle: 'normal',
    paddingBetweenTitleLabels: 20,
  }

  xAxisProp = {
    columnWidth: (this.slideWidth - 10) / 3,
    leftMargin: 10,
    columnCounter: 0,
  }

  yAxisProp = {
    rowTop: 0,
    headerMargin: 10,
  }

  articleImageProp = {
    imgSize: 150,
    imgHorSpacing: 9,
    imgVerSpacing: 7,
    imageLeft: 0,
    imageTop: 0,
    imageHeight: 500,
    imageWidth: 500,
    imageDefaultScaleFactor: merchConstants.slideImageDefaultScaleFactors.large,
  }

  articleAttributeProp = {
    fontWeight: 'normal',
    fontSize: merchConstants.slideArticleThumbnailFontSizes.large,
    textAlign: 'center',
  }

  slideTitleProps = {
    fontSize: 20,
    height: 35,
  }

  remainingHeight = this.slideHeight - this.yAxisProp.rowTop
  previousRowEnd = this.yAxisProp.rowTop
  previousQuadrantEnd = 0
  xAxisUniqueValues = {}
  yAxisUniqueValues = {}
  articleAttributesForCurrentPage: any[] = []
  // for options Data
  articleAttributes = []
  configurableArticleAttributes = []
  groupByArticleAttributes = []
  merchLabelAttributes: IMyAttribute[] = []
  mapOfSelectedArticleAttributesSystemNameToDisplayLabel = new Map()
  displayHorizontalAxis = true
  columnMap = {}
  isMerchFolderAvailable = false
  logoWidth = 180
  logoHeight = 180
  logoScaling = 0.6
  showInHeaderSkuCount = false
  showInHeaderStyleCount = false
  shouldShowHeaderByDefault = true
  fitInOnePageProps = {
    articleAttributeMaxHeight: 0,
    maximumRowInOnePage: 4,
    maxNumberOfColumns: 4,
    maximumArticleInRow: 0,
    maximumXAxisUniqueValues: 0,
    maximumYAxisUniqueValues: 0,
    articleMaxHeightText: '',
    scaleX: 1,
    scaleY: 1,
    maximumArticlesToConsiderForOneQuadrant: 50,
  }

  defaultHeaderLogoData
    = 'data:image/png;base64,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'

  headerLogoData: string = this.defaultHeaderLogoData
  isMerch: boolean = true
  quadrantCompleted: boolean = false
  rowCounterForEachYAxis: number = 0
  needOverLapping: boolean = false
  articlesCounterForEachYAxis: number = 0
  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>): IMerchTemplateOption[] {
    const attributes: IMyAttribute[] = []
    let configuredAttributes: IMyAttribute[] = []
    // VLPTemplateArticleAttributesToDisplay configuration Attributes to display which are provided as per configuration value
    const configuredVLPTemplateArticleAttributesToDisplay = catalog.Config.VLPTemplateArticleAttributesToDisplay
    for (const key in myAttributes) {
      attributes.push(myAttributes[key])
    }
    if (configuredVLPTemplateArticleAttributesToDisplay && configuredVLPTemplateArticleAttributesToDisplay.length !== 0) {
      configuredVLPTemplateArticleAttributesToDisplay.forEach((attributeSytemName) => {
        if (myAttributes[attributeSytemName]) {
          configuredAttributes.push(myAttributes[attributeSytemName])
        }
      })
    }
    else {
      configuredAttributes = attributes
    }

    const options: IMerchTemplateOption[] = []
    options.push({
      name: 'verticalAttributes',
      label: 'generateDialog.steps.options.yAxisAttributes',
      type: 'list',
      default: [],
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 3,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    options.push({
      name: 'horizontalAttributes',
      label: 'generateDialog.steps.options.xAxisAttributes',
      type: 'list',
      default: [],
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 3,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    // hide restricted attributes
    const ControlAttributeVisibilityToDisplayInOutput = catalog?.Config.ControlAttributeVisibilityToDisplayInOutput
    let allowedAttributes = configuredAttributes
    if (ControlAttributeVisibilityToDisplayInOutput && ControlAttributeVisibilityToDisplayInOutput.length) {
      allowedAttributes = configuredAttributes.filter((attr) => {
        return !ControlAttributeVisibilityToDisplayInOutput.includes(attr.SystemName)
      })
    }
    options.push({
      name: 'displayAttributes',
      label: 'generateDialog.steps.options.attributesToDisplay',
      type: 'attributeChooser',
      default: ['ArticleNumber'],
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 9,
      data: allowedAttributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    options.push({
      name: 'fitToOnePage',
      label: 'generateDialog.steps.options.fitToOnePage',
      type: 'bool',
      default: false,
      required: false,
    })

    const availableColumns: { key: number, label: number }[] = [
      { key: 1, label: 1 },
      { key: 2, label: 2 },
      { key: 3, label: 3 },
      { key: 4, label: 4 },
      { key: 5, label: 5 },
      { key: 6, label: 6 },
    ]

    options.push({
      name: 'numberOfColumns',
      label: 'generateFrameDialog.steps.options.numberOfColumns',
      type: 'list',
      default: 1,
      required: true,
      multiple: false,
      data: availableColumns,
      valueProp: 'key',
      displayProp: 'label',
      disabled: true,
    })

    const imageScales: IKeyLabel[] = []
    for (const key in merchConstants.slideImageDefaultScaleFactors) {
      imageScales.push({ key: merchConstants.slideImageDefaultScaleFactors[key], label: utils.capitalizeFirstLetter(key) })
    }

    options.push({
      name: 'imageScaleFactor',
      label: 'generateFrameDialog.steps.options.imageSize',
      type: 'list',
      default: merchConstants.slideImageDefaultScaleFactors.small,
      required: true,
      multiple: false,
      data: imageScales,
      valueProp: 'key',
      displayProp: 'label',
    })
    options.push({
      name: 'axisLabelFontSize',
      label: 'generateFrameDialog.steps.options.axisLabelFontSize',
      type: 'number',
      default: 15,
      required: true,
      multiple: false,
      min: 5,
      max: 25,
    })

    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: 'firstImage',
      data: merchConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })

    const availableSlideSizes: IKeyLabel[] = []
    for (const key in merchConstants.slideSize) {
      availableSlideSizes.push({ key, label: merchConstants.slideSize[key] })
    }
    options.push({
      name: 'slideSize',
      label: 'generateDialog.steps.options.slideSize',
      type: 'list',
      default: 'wideScreen',
      required: true,
      multiple: false,
      data: availableSlideSizes,
      valueProp: 'key',
      displayProp: 'label',
    })

    options.push({
      name: 'groupBy',
      label: 'generateDialog.steps.options.groupBy',
      type: 'list',
      default: [],
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 1,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    options.push({
      name: 'titleOnEachSlide',
      label: 'generateDialog.steps.options.titleOnEachSlide',
      type: 'bool',
      disabled: false,
      required: false,
    })

    options.push({
      name: 'slideTitle',
      label: 'generateDialog.steps.options.slideTitle',
      type: 'string',
      default: '',
      disabled: true,
      required: false,
    })

    options.push({
      name: 'sortAttributes',
      label: 'generateDialog.steps.options.sortAttributes',
      type: 'list',
      default: [],
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    const availableSortOrder: { key: string, label: string }[] = [
      { key: 'ascending', label: 'Ascending' },
      { key: 'descending', label: 'Descending' },
    ]
    options.push({
      name: 'sortOrder',
      label: 'generateDialog.steps.options.sortOrder',
      type: 'list',
      default: 'ascending',
      required: true,
      multiple: false,
      data: availableSortOrder,
      valueProp: 'key',
      displayProp: 'label',
      disabled: true,
    })
    options.push({
      name: 'groupByModel',
      label: 'generateDialog.steps.options.groupByModel',
      type: 'bool',
      required: false,
      default: false,
    })
    options.push({
      name: 'showSkuCount',
      label: 'generateDialog.steps.options.showSkuCount',
      type: 'bool',
      required: false,
      default: false,
      disabled: true,
    })
    return options
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    const mapping: IMerchTemplateFileMapping[] = []

    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code', 'colorwaynumber', 'colorway number'],
    })
    return mapping
  }

  resetProps() {
    this.xAxisProp = {
      columnWidth: (this.slideWidth - 10) / 3,
      leftMargin: 10,
      columnCounter: 0,
    }
    this.yAxisProp = {
      rowTop: 0,
      headerMargin: 10,
    }
    this.articleImageProp = {
      imgSize: 150,
      imgHorSpacing: 9,
      imgVerSpacing: 7,
      imageLeft: 0,
      imageTop: 0,
      imageHeight: 500,
      imageWidth: 500,
      imageDefaultScaleFactor: merchConstants.slideImageDefaultScaleFactors.large,
    }
    this.articleAttributeProp = {
      fontWeight: 'normal',
      fontSize: merchConstants.slideArticleThumbnailFontSizes.large,
      textAlign: 'center',
    }
    this.fitInOnePageProps = {
      articleAttributeMaxHeight: 0,
      maximumRowInOnePage: 4,
      maxNumberOfColumns: 4,
      maximumArticleInRow: 0,
      maximumXAxisUniqueValues: 0,
      maximumYAxisUniqueValues: 0,
      maximumArticlesToConsiderForOneQuadrant: 50,
      articleMaxHeightText: '',
      scaleX: 1,
      scaleY: 1,
    }
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, saveViewVLBData?: any[] | undefined): Promise<(void | number)> {
    // get header logo data
    return await this.getHeaderLogoData(catalog.CatalogCode).then(async (data) => {
      this.headerLogoData = data as string
      if (utils.isDefined(saveViewVLBData)) {
        return await this.buildFromSavedViews(catalog, saveViewVLBData, options, myUsername, myUserId, merch, myAttributes, retailPg, wholesalePg, outletPg)
      }
      else {
        return await this.buildTemplate(catalog, merch, articlesData, options, myAttributes, myUsername, myUserId, false, retailPg, wholesalePg, outletPg)
      }
    })
  }

  async buildFromSavedViews(catalog: CatalogDetails, savedViewData: any[], options: Record<string, any>, myUserName: string, myUserId: number, merch: Merch | undefined, myAttributes, retailPg, wholesalePg, outletPg) {
    const promises: Promise<void | number>[] = []
    let createdSlideCounts = 0

    savedViewData.sort((a, b) => {
      if (a.options.slideTitle.toLowerCase() > b.options.slideTitle.toLowerCase()) {
        return 1
      }
      else if (a.options.slideTitle.toLowerCase() < b.options.slideTitle.toLowerCase()) {
        return -1
      }
      else {
        return 0
      }
    })

    for (let i = 0; i < savedViewData.length; i++) {
      const filteredArticles: MyArticle[] = []
      const savedView = savedViewData[i]
      const viewOptions = Object.assign({}, options, savedView.options)
      viewOptions.titleOnEachSlide = true
      let savedViewHorizontalUniqueValues: string[] = []

      savedView.articles.forEach((article) => {
        // filter inactive article if inactive article should not be included
        if (article.Status === 0) {
          return
        }

        if (viewOptions.verticalAttributes.includes('_FavoriteTags') || viewOptions.horizontalAttributes.includes('_FavoriteTags')) {
          // As discussed with Sohail, if horizontal or vertical attributes contains the '_FavoriteTags' then ignore the articles which doesn't have any fav tag.
          // Also, filter the articles depending on the option value of include inactive articles
          if (article._FavoriteTags.length !== 0) {
            filteredArticles.push(cloneDeep(article))
            // identify x-axis unique value
            savedViewHorizontalUniqueValues = article._FavoriteTags.map(favTag => favTag.Tag)
          }
        }
        else {
          filteredArticles.push(cloneDeep(article))

          // identify x-axis unique value, in case of saved view there will be only one horizontalAttributes
          const horizontalAttribute = viewOptions.horizontalAttributes[0]
          let xAxisAttributeValue = utils.isDefined(article[horizontalAttribute]) ? article[horizontalAttribute] : ''
          if (horizontalAttribute != null && (horizontalAttribute.toLowerCase() === 'status')) {
            xAxisAttributeValue = xAxisAttributeValue === 1 ? 'Active' : 'Inactive'
          }
          let xAxisAttributeDisplayValue = xAxisAttributeValue.toString().toLowerCase().trim()
          xAxisAttributeDisplayValue = xAxisAttributeDisplayValue !== '' ? xAxisAttributeDisplayValue : '[blank]'

          if (!savedViewHorizontalUniqueValues.includes(xAxisAttributeDisplayValue)) {
            savedViewHorizontalUniqueValues.push(xAxisAttributeDisplayValue)
          }
        }
      })

      // set number of columns
      viewOptions.numberOfColumns = Math.min(Math.max(savedViewHorizontalUniqueValues.length, 1), 6)

      promises.push(this.buildTemplate(catalog, merch, filteredArticles, viewOptions, myAttributes, myUserName, myUserId, true, retailPg, wholesalePg, outletPg)
        .then((count) => {
          createdSlideCounts += count || 0
        }))
    }
    return Promise.all(promises).then(() => createdSlideCounts)
  }

  async buildTemplate(catalog: CatalogDetails, merch: Merch | undefined, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, showSlideTitleCounter: boolean, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup) {
    this.resetProps()
    const promises: Promise<void | number>[] = []
    const selectedXAttributes = options.horizontalAttributes || []
    const selectedYAttributes = options.verticalAttributes || []
    const articleAttributes = options.displayAttributes || []
    const imageScaleFactor = options.imageScaleFactor
    const sortAttributes = options.sortAttributes
    const sortOrder = options.sortOrder
    const slideSize = options.slideSize
    // this.headerLogoData = this.defaultHeaderLogoData
    // set the fontsize
    this.headerProp.fontSize = options.axisLabelFontSize ? options.axisLabelFontSize * 96 / 72 : 20
    this.slideTitleProps.fontSize = 20
    // Check if their is any horizontal attributes
    if (selectedXAttributes.length === 0) {
      this.yAxisProp.rowTop = 0
      this.displayHorizontalAxis = false
      this.fitInOnePageProps.maxNumberOfColumns = 1
      this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant = 100
    }
    else {
      this.yAxisProp.rowTop = this.headerHeight
      this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant = 50
      this.displayHorizontalAxis = true
    }
    if (selectedYAttributes.length === 0) {
      this.fitInOnePageProps.maximumRowInOnePage = 1
    }
    // get showStyleCount user setting
    // if (utils.isDefined(userSavedMerchSettings.showStyleCount) && userSavedMerchSettings.showStyleCount) {
    //   this.showInHeaderStyleCount = true
    // }
    // else {
    //   this.showInHeaderStyleCount = false
    // }

    // // get showSKUCount user setting
    // if (utils.isDefined(userSavedMerchSettings.showSKUCount) && userSavedMerchSettings.showSKUCount) {
    //   this.showInHeaderSkuCount = true
    // }
    // else {
    //   this.showInHeaderSkuCount = false
    // }

    // Don't show row if yAttributes are empty, skuCount and styleCount is disabled
    // if (selectedYAttributes.length === 0 && !this.showInHeaderSkuCount && !this.showInHeaderStyleCount) {
    //   this.shouldShowHeaderByDefault = false
    // }
    // Set slide size as per user selected
    this.changePropsAsPerSlideSize(slideSize, options.fitToOnePage ? this.fitInOnePageProps.maxNumberOfColumns : options.numberOfColumns)

    // find the attributes which should be visible with label
    this.findAndSetShowLabelMerchAttributes(catalog, Object.values(myAttributes))

    // create map of attribute system name to label used for calculating height article attributes
    this.createMapForArticleAttributes(articleAttributes)

    let numberOfArticlesEachColumn = 0
    if (!options.fitToOnePage) {
      // set the image size and font depending on imagescalefactor selected by user
      this.changePropsAsPerImageScaleFactor(imageScaleFactor)
      // Find number of articles in each column
      numberOfArticlesEachColumn = this.findNumberOfArticlesInEachColumn(options.numberOfColumns)
    }

    // if groupByModel is true get 1 article for each model number
    if (options.groupByModel) {
      articles = await this.groupArticlesByModelNumber(articles, catalog.CatalogCode)
    }
    const groups = await this.generateYAxisToXAxisMapping(catalog, myAttributes, myUsername, myUserId, selectedXAttributes, selectedYAttributes, articleAttributes, articles, sortAttributes, sortOrder, options, retailPg, wholesalePg, outletPg)
    let eachCellHeight = 0
    if (options.fitToOnePage) {
      if (selectedXAttributes.length) {
        this.fitInOnePageProps.maxNumberOfColumns = this.fitInOnePageProps.maximumXAxisUniqueValues > this.fitInOnePageProps.maxNumberOfColumns ? this.fitInOnePageProps.maxNumberOfColumns : this.fitInOnePageProps.maximumXAxisUniqueValues
      }
      if (selectedYAttributes.length) {
        this.fitInOnePageProps.maximumRowInOnePage = this.fitInOnePageProps.maximumYAxisUniqueValues > this.fitInOnePageProps.maximumRowInOnePage ? this.fitInOnePageProps.maximumRowInOnePage : this.fitInOnePageProps.maximumYAxisUniqueValues
      }
      eachCellHeight = Math.floor((this.slideHeight - (this.logoHeight * this.logoScaling) - ((this.headerHeight + this.yAxisProp.headerMargin) * (this.fitInOnePageProps.maximumRowInOnePage + 1))) / this.fitInOnePageProps.maximumRowInOnePage)
      this.findTheScalingFactor(this.fitInOnePageProps.maximumArticleInRow > this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant ? this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant : this.fitInOnePageProps.maximumArticleInRow, this.fitInOnePageProps.scaleX, this.fitInOnePageProps.scaleY, this.xAxisProp.columnWidth, eachCellHeight)
      this.articleImageProp.imgSize = this.articleImageProp.imgSize * this.fitInOnePageProps.scaleX
      // find the fontsize of the articleDetails
      if (articleAttributes.length) {
        // from the cell height - image size and calculate the font size so that article max height can be accomodated in the remaining space
        this.articleAttributeProp.fontSize = this.findFontSizeDependingOnTheHeight(this.fitInOnePageProps.scaleX, this.articleAttributeProp.fontSize, eachCellHeight - this.articleImageProp.imgSize)
      }
    }
    const props = {
      articleAttributes,
      slideNumber: 0,
      pageNumber: 0,
      articlesToDrawPerLine: numberOfArticlesEachColumn,
      rowMaxHeight: 0,
    }
    const currentObjects: any[] = []
    let titleOnEachSlide = options.titleOnEachSlide
    let slideTitle = options.slideTitle
    if (options.groupBy.length !== 0) {
      titleOnEachSlide = true
    }
    if (options.fitToOnePage) {
      groups.forEach((value, key) => {
        const yAxisToxAxisMap = value.yAxisToxAxisMap
        this.xAxisProp.columnCounter = 0
        const xAxisUniqueValues = this.xAxisUniqueValues[key]
        const yAxisUniqueValues = this.yAxisUniqueValues[key]
        const minimumNumberOfPagesAsPerColumns = Math.ceil(xAxisUniqueValues.length / this.fitInOnePageProps.maxNumberOfColumns)
        if (options.groupBy.length !== 0) {
          slideTitle = key
        }
        // TODO: pivot at first glance the logic does not look correct to me
        for (let numberOfPagesCounter = 0; numberOfPagesCounter < minimumNumberOfPagesAsPerColumns; numberOfPagesCounter++) {
          const currentColumns = this.getColumnsForPage(xAxisUniqueValues, this.fitInOnePageProps.maxNumberOfColumns)
          this.xAxisProp.columnCounter += this.fitInOnePageProps.maxNumberOfColumns
          const validYAxisValuesMap = {}
          for (let yAxisUniqueValuesIndex = 0; yAxisUniqueValuesIndex < yAxisUniqueValues.length; yAxisUniqueValuesIndex++) {
            for (const columnNumber in currentColumns) {
              if (yAxisToxAxisMap.get(yAxisUniqueValues[yAxisUniqueValuesIndex].yAxisSep).xAxisGroup.has(currentColumns[columnNumber].xAxisSep)) {
                if (!validYAxisValuesMap[yAxisUniqueValues[yAxisUniqueValuesIndex].yAxisSep]) {
                  validYAxisValuesMap[yAxisUniqueValues[yAxisUniqueValuesIndex].yAxisSep] = yAxisUniqueValues[yAxisUniqueValuesIndex]
                }
              }
            }
          }
          let numberOfRowsConsideredCount = 0
          while (numberOfRowsConsideredCount < (Object.values(validYAxisValuesMap).length === 1 ? Object.values(validYAxisValuesMap).length : Object.values(validYAxisValuesMap).length - 1)) {
            const currentYAxisValues: any = Object.values(validYAxisValuesMap).slice(numberOfRowsConsideredCount, numberOfRowsConsideredCount + this.fitInOnePageProps.maximumRowInOnePage)
            // set props.rowMaxHeight as per currentYAxisValues
            eachCellHeight = Math.floor((this.slideHeight - (this.logoHeight * this.logoScaling) - ((this.headerHeight + this.yAxisProp.headerMargin) * (currentYAxisValues.length + 1))) / currentYAxisValues.length)
            for (let yAxisIndex = 0; yAxisIndex < currentYAxisValues.length; yAxisIndex++) {
              if (yAxisIndex === 0) {
                this.onNewPage(myAttributes, currentObjects, currentColumns, selectedXAttributes, titleOnEachSlide, slideTitle, showSlideTitleCounter, props)
              }
              currentObjects.push(...this.createYAxisColumnLabelObjectToHeader(yAxisToxAxisMap.get(currentYAxisValues[yAxisIndex].yAxisSep).yAxisHeaderInfo, currentYAxisValues[yAxisIndex], selectedYAttributes, myAttributes))
              this.yAxisProp.rowTop += this.headerHeight + this.yAxisProp.headerMargin
              for (const colNumber in currentColumns) {
                if (yAxisToxAxisMap.get(currentYAxisValues[yAxisIndex].yAxisSep).xAxisGroup.has(currentColumns[colNumber].xAxisSep)) {
                  const mapDetails = yAxisToxAxisMap.get(currentYAxisValues[yAxisIndex].yAxisSep).xAxisGroup.get(currentColumns[colNumber].xAxisSep)
                  // draw the articles with articleDetails maximum articles to draw is defined at this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant
                  const validArticles = mapDetails.articleInfo.articles.slice(0, this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant)
                  this.drawPivotFitToOnePageArticles(currentObjects, validArticles, colNumber, options.imageType, this.xAxisProp.columnWidth, eachCellHeight, props, options)
                }
              }
              this.yAxisProp.rowTop += eachCellHeight
            }
            if (currentObjects.length !== 0) {
              if (this.isMerch) {
                promises.push(this.createSlide(merch, currentObjects.slice(), titleOnEachSlide, props, slideTitle, options, [])
                  .catch((error) => {
                    // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                    console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
                  }))
              }
            }
            numberOfRowsConsideredCount += this.fitInOnePageProps.maximumRowInOnePage
          }
        }
      })
    }
    else {
      groups.forEach((value, key) => {
        const yAxisToxAxisMap = value.yAxisToxAxisMap
        this.xAxisProp.columnCounter = 0
        const xAxisUniqueValues = this.xAxisUniqueValues[key]
        const yAxisUniqueValues = this.yAxisUniqueValues[key]
        const minimumNumberOfPagesAsPerColumns = Math.ceil(xAxisUniqueValues.length / options.numberOfColumns)
        if (options.groupBy.length !== 0) {
          slideTitle = key
        }
        // TODO: pivot at first glance the logic does not look correct to me
        for (let numberOfPagesCounter = 0; numberOfPagesCounter < minimumNumberOfPagesAsPerColumns; numberOfPagesCounter++) {
          const currentColumns = this.getColumnsForPage(xAxisUniqueValues, options.numberOfColumns)
          if (numberOfPagesCounter !== 0) {
            // for new columns new page - n before creating new page add current Article attributes if any
            for (const article in this.articleAttributesForCurrentPage) {
              currentObjects.push(...this.createArticleAttributes(this.articleAttributesForCurrentPage[article].article, props, this.articleAttributesForCurrentPage[article].top + this.articleImageProp.imgVerSpacing, this.articleAttributesForCurrentPage[article].left, options.groupByModel, options.showSkuCount))
            }
            this.articleAttributesForCurrentPage = []
            if (this.isMerch) {
              promises.push(this.createSlide(merch, currentObjects.slice(), titleOnEachSlide, props, slideTitle, options, [])
                .catch((error) => {
                  // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                  console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
                }))
            }
          }
          if (utils.isDefined(currentColumns) && currentColumns.length !== 0) {
            this.onNewPage(myAttributes, currentObjects, currentColumns, selectedXAttributes, titleOnEachSlide, slideTitle, showSlideTitleCounter, props)
          }

          this.xAxisProp.columnCounter += options.numberOfColumns
          for (let yAxisUniqueValuesIndex = 0; yAxisUniqueValuesIndex < yAxisUniqueValues.length; yAxisUniqueValuesIndex++) {
            // Should add only selectedYAttributes are not empty and skuCount or styleCount is enabled
            let shouldAddYAxisHeader = this.shouldShowHeaderByDefault // this denotes after every new Y axis value this will reset and use to add the add the y-axis header
            let addYAxisHeader = false // this will inside the loop of while once added should not be rendered after every row, and also for new page if we are carry forwarding the same headers
            let addYAxisLabel = false
            // find articles for y and 3 axis
            this.rowCounterForEachYAxis = 0
            this.articlesCounterForEachYAxis = 0
            this.quadrantCompleted = false
            const quadrantCompletedMap = []
            for (const columnNumber in currentColumns) {
              quadrantCompletedMap[columnNumber] = false
            }
            // if any column
            if (currentColumns) {
              this.previousQuadrantEnd = this.yAxisProp.rowTop
              // loop for all the quadrants for selected y-value - for 3 columns  is rendered
              while (!this.quadrantCompleted) {
                this.columnMap = {}
                props.rowMaxHeight = 0
                let newRow = false
                // set previous row end
                this.previousRowEnd = this.yAxisProp.rowTop
                for (const columnNumber in currentColumns) {
                  this.columnMap[columnNumber] = []
                  if (yAxisToxAxisMap.get(yAxisUniqueValues[yAxisUniqueValuesIndex].yAxisSep).xAxisGroup.has(currentColumns[columnNumber].xAxisSep)) {
                    const mapDetails = yAxisToxAxisMap.get(yAxisUniqueValues[yAxisUniqueValuesIndex].yAxisSep).xAxisGroup.get(currentColumns[columnNumber].xAxisSep)
                    const articlesToDraw = mapDetails.articleInfo.articles.slice(this.articlesCounterForEachYAxis, this.articlesCounterForEachYAxis + props.articlesToDrawPerLine)
                    if (articlesToDraw.length !== 0) {
                      if (shouldAddYAxisHeader) {
                        addYAxisHeader = true
                      }
                      for (let i = 0; i < articlesToDraw.length; i++) {
                        if (props.rowMaxHeight < (articlesToDraw[i]._maxHeight + articlesToDraw[i]._skuCountHeight)) {
                          props.rowMaxHeight = articlesToDraw[i]._maxHeight + articlesToDraw[i]._skuCountHeight
                        }
                      }
                      this.columnMap[columnNumber] = articlesToDraw
                    }
                  }
                }
                for (const columnNumber in currentColumns) {
                  if (this.columnMap[columnNumber].length !== 0) {
                    if (this.remainingHeight < (this.articleImageProp.imgVerSpacing + this.articleImageProp.imgSize + props.rowMaxHeight + this.headerHeight + this.yAxisProp.headerMargin)) {
                      if (!this.quadrantCompleted && addYAxisLabel) {
                        // Should add only selectedYAttributes are not empty and skuCount or styleCount is enabled
                        addYAxisHeader = this.shouldShowHeaderByDefault
                        addYAxisLabel = false
                      }
                      // if Remaining page cannot accommodate new Row
                      addYAxisLabel = false
                      for (const article in this.articleAttributesForCurrentPage) {
                        currentObjects.push(...this.createArticleAttributes(this.articleAttributesForCurrentPage[article].article, props, this.articleAttributesForCurrentPage[article].top + this.articleImageProp.imgVerSpacing, this.articleAttributesForCurrentPage[article].left, options.groupByModel, options.showSkuCount))
                      }
                      this.articleAttributesForCurrentPage = []
                      if (this.isMerch) {
                        promises.push(this.createSlide(merch, currentObjects.slice(), titleOnEachSlide, props, slideTitle, options, [])
                          .catch((error) => {
                          // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                            console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
                          }))
                      }
                      this.onNewPage(myAttributes, currentObjects, currentColumns, selectedXAttributes, titleOnEachSlide, slideTitle, showSlideTitleCounter, props)
                      if (addYAxisHeader) {
                        currentObjects.push(...this.createYAxisColumnLabelObjectToHeader(yAxisToxAxisMap.get(yAxisUniqueValues[yAxisUniqueValuesIndex].yAxisSep).yAxisHeaderInfo, yAxisUniqueValues[yAxisUniqueValuesIndex], selectedYAttributes, myAttributes))
                        this.yAxisProp.rowTop += this.headerHeight + this.yAxisProp.headerMargin
                        shouldAddYAxisHeader = false
                        addYAxisHeader = false
                      }
                    }
                    if (addYAxisHeader) {
                      currentObjects.push(...this.createYAxisColumnLabelObjectToHeader(yAxisToxAxisMap.get(yAxisUniqueValues[yAxisUniqueValuesIndex].yAxisSep).yAxisHeaderInfo, yAxisUniqueValues[yAxisUniqueValuesIndex], selectedYAttributes, myAttributes))
                      this.yAxisProp.rowTop += this.headerHeight + this.yAxisProp.headerMargin
                      shouldAddYAxisHeader = false
                      addYAxisHeader = false
                    }
                    addYAxisLabel = true
                    newRow = true
                    this.createArticleImagesWithAttribute(currentObjects, this.columnMap[columnNumber], columnNumber, options.imageType)
                  }
                  else {
                    quadrantCompletedMap[columnNumber] = true
                  }
                }
                this.quadrantCompleted = true
                for (const columnNumber in currentColumns) {
                  if (quadrantCompletedMap[columnNumber] === false) {
                    this.quadrantCompleted = false
                  }
                }
                if (this.quadrantCompleted) {
                  // Should add only selectedYAttributes are not empty and skuCount or styleCount is enabled
                  shouldAddYAxisHeader = this.shouldShowHeaderByDefault
                  addYAxisHeader = false
                }
                if (newRow === true) {
                  this.yAxisProp.rowTop += this.articleImageProp.imgVerSpacing + this.articleImageProp.imgSize + props.rowMaxHeight
                  if (props.articleAttributes.length !== 0) {
                    this.yAxisProp.rowTop += 10
                  }
                  newRow = false
                }
                this.remainingHeight = this.remainingHeight - (this.yAxisProp.rowTop - this.previousRowEnd)
                this.articlesCounterForEachYAxis += props.articlesToDrawPerLine
              }
            }
            else {
              // no columns
            }
            if (addYAxisLabel === true) {
              addYAxisLabel = false
            }
          }
        }
        if (currentObjects.length !== 0) {
          // last slide Data - Also render article Attributes when completed
          for (const article in this.articleAttributesForCurrentPage) {
            currentObjects.push(...this.createArticleAttributes(this.articleAttributesForCurrentPage[article].article, props, this.articleAttributesForCurrentPage[article].top + this.articleImageProp.imgVerSpacing, this.articleAttributesForCurrentPage[article].left, options.groupByModel, options.showSkuCount))
          }
          this.articleAttributesForCurrentPage = []
          if (this.isMerch) {
            promises.push(this.createSlide(merch, currentObjects.slice(), titleOnEachSlide, props, slideTitle, options, [])
              .catch((error) => {
              // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
              }))
          }
        }
      })
    }
    return Promise.all(promises).then(() => {
      if (this.isMerch) {
        return props.slideNumber
      }
    })
  }

  createMapForArticleAttributes(selectedArticleAttributes: string[]) {
    for (let index = 0; index < selectedArticleAttributes.length; index++) {
      const attributeObject = this.merchLabelAttributes.find(attribute => attribute.SystemName === selectedArticleAttributes[index])
      if (utils.isDefined(attributeObject)) {
        this.mapOfSelectedArticleAttributesSystemNameToDisplayLabel.set(selectedArticleAttributes[index], attributeObject.DisplayName)
      }
    }
  }

  async groupArticlesByModelNumber(articles: MyArticle[], catalogCode: number) {
    const modelArticles: MyArticle[] = []
    const articlesClone = cloneDeep(articles)
    // sort all articles by article number
    utils.sort(articlesClone, ['ArticleNumber'])
    const modelNumbers = Array.from(new Set(articles.map(article => article.ModelNumber)))
    const modelCountMap: Record<string, number> = {}
    for (let i = 0; i < modelNumbers.length; i++) {
      modelCountMap[modelNumbers[i]] = await appConfig.DB!.getModelSKUCount(modelNumbers[i], catalogCode)
    }
    articlesClone.forEach((article) => {
      const index = modelNumbers.findIndex(modelNumber => modelNumber === article.ModelNumber)
      if (index !== -1) {
        article._skuCount = modelCountMap[article.ModelNumber]
        modelArticles.push(article)
        modelNumbers.splice(index, 1)
      }
    })
    return modelArticles
  }

  generateSortOrderString(article: MyArticle, sortOrderAttributes: string[], myAttributes: Record<string, IMyAttribute>) {
    let sortOrderString = ''
    sortOrderAttributes.forEach((sortOrderAttribute) => {
      let attributeValue = article[sortOrderAttribute]
      if (sortOrderAttribute.toLowerCase() === '_retailprice'
        || sortOrderAttribute.toLowerCase() === '_wholesaleprice'
        || sortOrderAttribute.toLowerCase() === '_outletprice') {
        const articlePriceDetails = article[sortOrderAttribute]
        attributeValue = articlePriceDetails != null ? articlePriceDetails : ''
      }
      else if (sortOrderAttribute === '_favoriteTags') {
        const tagNames: string[] = []
        article._FavoriteTags.forEach((tagObject) => {
          tagNames.push(tagObject.Tag)
        })
        // we will take the first sorted result in order to maintain the sorting by article number
        const sortedTagNames = tagNames.sort()
        // adding '~' to sort the articles with tags at last
        // TODO: may need to implement separate comparer
        attributeValue = sortedTagNames.length > 0 ? `~${sortedTagNames[0]}` : ''
      }
      else if (myAttributes[sortOrderAttribute] && myAttributes[sortOrderAttribute].AttributeType === AttributeType.MultiValue) {
        const multiValues = article[sortOrderAttribute] || []
        const sortedValues = Array.isArray(multiValues) ? multiValues.sort() : []
        attributeValue = sortedValues.length > 0 ? `~${sortedValues[0]}` : ''
        //   } else if (sortOrderAttribute.toLowerCase() === '_firstdeliverydate') {
        // attributeValue = attributeValue && attributeValue.toString().trim() !== '' ? attributeValue.getTime() : ''
      }
      if (typeof attributeValue === 'number') {
        // for correct comparison of number string
        const numberParts = attributeValue.toString().split('.')
        sortOrderString += numberParts[0].toString().padStart(10, '0')
        if (numberParts[1] != null) {
          sortOrderString += `.${numberParts[1].toString().padEnd(5, '0')}`
        }
      }
      else {
        sortOrderString += attributeValue || ''
      }
    })
    return sortOrderString
  }

  getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg?, wholesalePg?, outletPg?) {
    let axisSep = ''
    let sortOrderForAxis = ''
    for (let attributeIndex = 0; attributeIndex < selectedXAttributes.length; attributeIndex++) {
      // Add the sep if not first value in xAxis
      if (attributeIndex !== 0) {
        axisSep += ' / '
      }

      if (selectedXAttributes[attributeIndex] === '_FavoriteTags') {
        axisSep += favObject.tagName
        sortOrderForAxis += `${this.getSortOrder(selectedXAttributes[favObject.xAttributesFavTagsIndex], favObject.tagName)}`
      }
      else if (selectedXAttributes[attributeIndex] === '_Segmentation') {
        axisSep += segmentationObject.segmentation
        sortOrderForAxis += `${this.getSortOrder(selectedXAttributes[segmentationObject.xAttributesSegIndex], segmentationObject.segmentation)}`
      }
      else if (utils.isDefined(xAxisMultiValueAttributeValuesMap[selectedXAttributes[attributeIndex]])) {
        axisSep += currentMultiValueObject.currentMultiValue
        sortOrderForAxis += `${this.getSortOrder(selectedXAttributes[attributeIndex], currentMultiValueObject.currentMultiValue)}`
      }
      else {
        let valueAtPath = get(article, selectedXAttributes[attributeIndex], '[blank]') || '[blank]'
        if (selectedXAttributes[attributeIndex].toLowerCase() === '_retailprice') {
          valueAtPath = utils.isDefined(valueAtPath) ? utils.formatPrice(retailPg, Number(valueAtPath), catalog?.Config.ShowPriceThousandsSeparated) : '[blank]'
        }
        else if (selectedXAttributes[attributeIndex].toLowerCase() === '_wholesaleprice') {
          valueAtPath = utils.isDefined(valueAtPath) ? utils.formatPrice(wholesalePg, Number(valueAtPath), catalog?.Config.ShowPriceThousandsSeparated) : '[blank]'
        }
        else if (selectedXAttributes[attributeIndex].toLowerCase() === '_outletprice') {
          valueAtPath = utils.isDefined(valueAtPath) ? utils.formatPrice(outletPg, Number(valueAtPath), catalog?.Config.ShowPriceThousandsSeparated) : '[blank]'
        }
        else if (selectedXAttributes[attributeIndex].toLowerCase() === 'colorid') {
          valueAtPath = utils.isDefined(valueAtPath) ? article.ColorName ? article.ColorName : valueAtPath : '[blank]'
        }

        else if (selectedXAttributes[attributeIndex].toLowerCase() === 'status') {
          valueAtPath = valueAtPath === 1 ? 'Active' : 'Inactive'
        }
        else if (selectedXAttributes[attributeIndex].toLowerCase() === 'locked') {
          valueAtPath = valueAtPath === 1 ? 'True' : 'False'
        }
        const xAxisAttributeDisplayValue = valueAtPath.toString().toLowerCase().trim()
        axisSep += xAxisAttributeDisplayValue !== '' ? `${xAxisAttributeDisplayValue}` : '[blank]'
        sortOrderForAxis += `${this.getSortOrder(selectedXAttributes[attributeIndex], valueAtPath)}`
      }
    }
    return { axisSep, sortOrderForAxis }
  }

  async generateYAxisToXAxisMapping(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, myUserName: string, myUserId: number, selectedXAttributes, selectedYAttributes, articleAttributes, articles, sortAttributes, sortOrder, options: Record<string, any>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup) {
    const isGroupByModel = options.groupByModel
    const isShowSkuCount = options.showSkuCount
    const fitToOnePage = options.fitToOnePage
    const groupByAttribute = options.groupBy
    const groups = new Map()
    // find xAttributes and YAttributes has '_FavoriteTags'
    const xAttributesFavTagsIndex = selectedXAttributes.findIndex(attribute => attribute === '_FavoriteTags')
    const yAxisAttributeFavIndex = selectedYAttributes.findIndex(attribute => attribute === '_FavoriteTags')
    // find xAttributes and YAttributes has '_Segmentation'
    const xAttributesSegIndex = selectedXAttributes.findIndex(attribute => attribute === '_Segmentation')
    const yAxisAttributeSegIndex = selectedYAttributes.findIndex(attribute => attribute === '_Segmentation')
    // find xAttributes and YAttributes has '_multi value'
    const multiValueXAttributes = selectedXAttributes.filter(attribute => myAttributes[attribute] && myAttributes[attribute].AttributeType === AttributeType.MultiValue)
    const multiValueYAttributes = selectedYAttributes.filter(attribute => myAttributes[attribute] && myAttributes[attribute].AttributeType === AttributeType.MultiValue)
    for (let i = 0; i < articles.length; i++) {
      const article = articles[i]
      let articleGroupByAttributeValues: any[] = []
      if (groupByAttribute.length !== 0) {
        let valueAtPath = get(article, groupByAttribute, '[Blank]') || '[Blank]'
        if (groupByAttribute === 'Status') {
          valueAtPath = valueAtPath === 1 ? 'Active' : 'Inactive'
        }
        else if (groupByAttribute === '_FavoriteTags') {
          valueAtPath = article._FavoriteTags && article._FavoriteTags.length ? article._FavoriteTags.map(favTag => favTag.Tag) : ['[Blank]']
        }
        else if (myAttributes[groupByAttribute] && myAttributes[groupByAttribute].AttributeType === AttributeType.Bool) {
          valueAtPath = valueAtPath === 1 ? 'True' : 'False'
        }
        // else if (groupByAttribute == '_firstDeliveryDate') {
        //   valueAtPath = valueAtPath.toString().trim() !== '' ? utils.formatDate(valueAtPath) : '[Blank]'
        // }
        else if (myAttributes[groupByAttribute] && myAttributes[groupByAttribute].AttributeType === AttributeType.LinkedCatalogArticleNumber) {
          const val = await utils.getAttributeTypeSpecificValue(myAttributes[groupByAttribute], article)
          if (val.toString() !== '') {
            valueAtPath = val
          }
        }
        // else if (myAttributes[groupByAttribute] && myAttributes[groupByAttribute].AttributeType === 'modelnumber') {
        //   valueAtPath = catalogData.indexedSellerModelsByModelId.hasOwnProperty(valueAtPath) ? catalogData.indexedSellerModelsByModelId[valueAtPath].ModelNumber : valueAtPath
        // }
        else if (myAttributes[groupByAttribute] && (myAttributes[groupByAttribute].AttributeType === AttributeType.ArticleNumber)) {
          const articles = await appConfig.DB!.getMyArticlesById(catalog, {}, myAttributes, myUserName, myUserId, Number(valueAtPath), retailPg, wholesalePg, outletPg)
          if (articles.length !== 0) {
            valueAtPath = articles[0].ArticleNumber
          }
        }
        if (myAttributes[groupByAttribute] && myAttributes[groupByAttribute].AttributeType === AttributeType.MultiValue) {
          if (utils.isDefined(valueAtPath)) {
            articleGroupByAttributeValues = valueAtPath
          }
          else {
            articleGroupByAttributeValues.push('[Blank]')
          }
        }
        // else if (groupByAttribute == '_crds') {
        //   if (utils.isDefined(valueAtPath)) {
        //     for (const crdId in valueAtPath) {
        //       if (valueAtPath[crdId].Status === 1) {
        //         const deliveryDate = new Date(valueAtPath[crdId].CustomerRequiredDate)
        //         articleGroupByAttributeValues.push(utils.formatDate(deliveryDate))
        //       }
        //     }
        //   }
        //   else {
        //     articleGroupByAttributeValues.push('[Blank]')
        //   }
        // }
        else {
          if (!utils.isDefined(valueAtPath)) {
            articleGroupByAttributeValues.push('[Blank]')
          }
          else {
            articleGroupByAttributeValues.push(valueAtPath)
          }
        }
      }
      else {
        articleGroupByAttributeValues.push('[Blank]')
      }
      for (let j = 0; j < articleGroupByAttributeValues.length; j++) {
        const articleGroupByAttributeValue = articleGroupByAttributeValues[j]
        if (!groups.has(articleGroupByAttributeValue)) {
          const groupData = {
            yAxisToxAxisMap: new Map(),
            xAxisValues: [],
            // dummy value if selected yAttributes are empty
            yAxisValues: selectedYAttributes.length ? [] : [{ yAxisSep: '_Dummy', sortOrder: '' }],
          }
          groups.set(articleGroupByAttributeValue, groupData)
        }
        const xAxisSepList: string[] = []
        // dummy seperator if selected yAttributes are empty
        const yAxisSepList = selectedYAttributes.length ? [] : ['_Dummy']
        const tagNames = article._favoriteTags && article._favoriteTags.length ? article._favoriteTags.map(favTag => favTag.Name) : []
        const xAxisMultiValueAttributeValuesMap: Record<string, string[]> = {}
        const yAxisMultiValueAttributeValuesMap: Record<string, string[]> = {}

        multiValueXAttributes.forEach((attribute) => {
          xAxisMultiValueAttributeValuesMap[attribute] = []
          const valueAtPath = get(article, attribute, []) || []
          let multiValues: any[] = []
          if (utils.isDefined(valueAtPath) && valueAtPath.length !== 0) {
            multiValues = valueAtPath
          }
          else {
            multiValues.push('[blank]')
          }
          for (let valueIndex = 0; valueIndex < multiValues.length; valueIndex++) {
            xAxisMultiValueAttributeValuesMap[attribute].push(multiValues[valueIndex])
          }
        })
        multiValueYAttributes.forEach((attribute) => {
          yAxisMultiValueAttributeValuesMap[attribute] = []
          const valueAtPath = get(article, attribute, []) || []
          let multiValues: any[] = []
          if (utils.isDefined(valueAtPath) && valueAtPath.length !== 0) {
            multiValues = valueAtPath
          }
          else {
            multiValues.push('[blank]')
          }
          for (let valueIndex = 0; valueIndex < multiValues.length; valueIndex++) {
            yAxisMultiValueAttributeValuesMap[attribute].push(multiValues[valueIndex])
          }
        })
        // If xAttributes has '_FavoriteTags' and article has tags we need to create multiple sep for each tag value to rest if the attributes value of an article
        if (xAttributesFavTagsIndex !== -1 && tagNames.length) {
          tagNames.forEach((tagName) => {
            if (xAttributesSegIndex !== -1) {
              const segmentations: string[] = []
              const articleSegmentation: ArticleSegmentation[] = Object.values(article._Segmentations)
              if (articleSegmentation.length) {
                // if (article._isOpenSegmented) {
                //   segmentations.push(def.openSegmentationObject.Name)
                // }
                articleSegmentation.forEach((articleSegmentation) => {
                  if (catalog._IndexedCatalogSegmentation[articleSegmentation.Id]
                    && !appConstants.excludeSegmentationNames.has(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name.toLowerCase())) {
                    segmentations.push(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name)
                  }
                })
              }
              else {
                segmentations.push('[None]')
              }
              segmentations.forEach((segmentation) => {
                if (multiValueXAttributes.length) {
                  multiValueXAttributes.forEach((attribute) => {
                    for (let valueIndex = 0; valueIndex < xAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                      const currentMultiValue = xAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                      const favObject = { xAttributesFavTagsIndex, tagName }
                      const segmentationObject = { xAttributesSegIndex, segmentation }
                      const currentMultiValueObject = { currentMultiValue }
                      const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                      groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                      xAxisSepList.push(axisSepData.axisSep)
                    }
                  })
                }
                else {
                  // no multi value
                  const favObject = { xAttributesFavTagsIndex, tagName }
                  const segmentationObject = { xAttributesSegIndex, segmentation }
                  const currentMultiValueObject = {}
                  const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)

                  groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                  xAxisSepList.push(axisSepData.axisSep)
                }
              })
            }
            else {
              if (multiValueXAttributes.length) {
                multiValueXAttributes.forEach((attribute) => {
                  for (let valueIndex = 0; valueIndex < xAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                    const currentMultiValue = xAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                    const favObject = { xAttributesFavTagsIndex, tagName }
                    const segmentationObject = {}
                    const currentMultiValueObject = { currentMultiValue }
                    const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                    groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                    xAxisSepList.push(axisSepData.axisSep)
                  }
                })
              }
              else {
                // no multi value
                const favObject = { xAttributesFavTagsIndex, tagName }
                const segmentationObject = {}
                const currentMultiValueObject = {}
                const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)

                groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                xAxisSepList.push(axisSepData.axisSep)
              }
            }
          })
        }
        else {
          // If xAttributes  doesn't  have the '_FavoriteTags' iterate over attribute list and find the sep
          if (xAttributesSegIndex !== -1) {
            const segmentations: string[] = []
            const articleSegmentation: ArticleSegmentation[] = Object.values(article._Segmentations)
            if (articleSegmentation.length) {
              // if (article._isOpenSegmented) {
              //   segmentations.push(def.openSegmentationObject.Name)
              // }
              articleSegmentation.forEach((articleSegmentation) => {
                if (catalog._IndexedCatalogSegmentation[articleSegmentation.Id]
                  && !appConstants.excludeSegmentationNames.has(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name.toLowerCase())) {
                  segmentations.push(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name)
                }
              })
            }
            else {
              segmentations.push('[None]')
            }
            segmentations.forEach((segmentation) => {
              if (multiValueXAttributes.length) {
                multiValueXAttributes.forEach((attribute) => {
                  for (let valueIndex = 0; valueIndex < xAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                    const currentMultiValue = xAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                    const favObject = {}
                    const segmentationObject = { xAttributesSegIndex, segmentation }
                    const currentMultiValueObject = { currentMultiValue }
                    const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                    groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                    xAxisSepList.push(axisSepData.axisSep)
                  }
                })
              }
              else {
                // no mulit value
                const favObject = {}
                const segmentationObject = { xAttributesSegIndex, segmentation }
                const currentMultiValueObject = {}
                const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)

                groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                xAxisSepList.push(axisSepData.axisSep)
              }
            })
          }
          else {
            if (multiValueXAttributes.length) {
              multiValueXAttributes.forEach((attribute) => {
                for (let valueIndex = 0; valueIndex < xAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                  const currentMultiValue = xAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                  const favObject = {}
                  const segmentationObject = {}
                  const currentMultiValueObject = { currentMultiValue }
                  const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                  groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                  xAxisSepList.push(axisSepData.axisSep)
                }
              })
            }
            else {
              // no multi value
              const favObject = {}
              const segmentationObject = {}
              const currentMultiValueObject = {}
              const axisSepData = this.getAxisSepValue(catalog, selectedXAttributes, article, favObject, segmentationObject, currentMultiValueObject, xAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)

              groups.get(articleGroupByAttributeValue).xAxisValues.push({ xAxisSep: axisSepData.axisSep, articleId: article.Id, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
              xAxisSepList.push(axisSepData.axisSep)
            }
          }
        }
        // Repeat if YAttributes are selected
        if (selectedYAttributes.length) {
          // Repeat the same as above for the y axis below
          if (yAxisAttributeFavIndex !== -1 && tagNames.length) {
            tagNames.forEach((tagId) => {
              if (yAxisAttributeSegIndex !== -1) {
                const segmentations: string[] = []
                const articleSegmentation: ArticleSegmentation[] = Object.values(article._Segmentations)
                if (articleSegmentation.length) {
                  // if (article._isOpenSegmented) {
                  //   segmentations.push(def.openSegmentationObject.Name)
                  // }
                  articleSegmentation.forEach((articleSegmentation) => {
                    if (catalog._IndexedCatalogSegmentation[articleSegmentation.Id]
                      && !appConstants.excludeSegmentationNames.has(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name.toLowerCase())) {
                      segmentations.push(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name)
                    }
                  })
                }
                else {
                  segmentations.push('[None]')
                }
                segmentations.forEach((segmentation) => {
                  if (multiValueYAttributes.length) {
                    multiValueYAttributes.forEach((attribute) => {
                      for (let valueIndex = 0; valueIndex < yAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                        const currentMultiValue = yAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                        const favObject = { yAxisAttributeSegIndex, tagId }
                        const segmentationObject = { xAttributesSegIndex, segmentation }
                        const currentMultiValueObject = { currentMultiValue }
                        const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                        groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                        yAxisSepList.push(axisSepData.axisSep)
                      }
                    })
                  }
                  else {
                    // no multi value
                    const favObject = { yAxisAttributeSegIndex, tagId }
                    const segmentationObject = { yAxisAttributeSegIndex, segmentation }
                    const currentMultiValueObject = {}
                    const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                    groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                    yAxisSepList.push(axisSepData.axisSep)
                  }
                })
              }
              else {
                if (multiValueYAttributes.length) {
                  multiValueYAttributes.forEach((attribute) => {
                    for (let valueIndex = 0; valueIndex < yAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                      const currentMultiValue = yAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                      const favObject = { yAxisAttributeSegIndex, tagId }
                      const segmentationObject = {}
                      const currentMultiValueObject = { currentMultiValue }
                      const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                      groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                      yAxisSepList.push(axisSepData.axisSep)
                    }
                  })
                }
                else {
                  // no multi value
                  const favObject = { yAxisAttributeSegIndex, tagId }
                  const segmentationObject = {}
                  const currentMultiValueObject = {}
                  const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                  groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                  yAxisSepList.push(axisSepData.axisSep)
                }
              }
            })
          }
          else {
            // If yAttributes  doesn't  have the '_FavoriteTags' iterate over attribute list and find the sep
            if (yAxisAttributeSegIndex !== -1) {
              const segmentations: string[] = []
              const articleSegmentation: ArticleSegmentation[] = Object.values(article._Segmentations)
              if (articleSegmentation.length) {
                // if (article._isOpenSegmented) {
                //   segmentations.push(def.openSegmentationObject.Name)
                // }
                articleSegmentation.forEach((articleSegmentation) => {
                  if (catalog._IndexedCatalogSegmentation[articleSegmentation.Id]
                    && !appConstants.excludeSegmentationNames.has(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name.toLowerCase())) {
                    segmentations.push(catalog._IndexedCatalogSegmentation[articleSegmentation.Id].Name)
                  }
                })
              }
              else {
                segmentations.push('[None]')
              }
              segmentations.forEach((segmentation) => {
                if (multiValueYAttributes.length) {
                  multiValueYAttributes.forEach((attribute) => {
                    for (let valueIndex = 0; valueIndex < yAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                      const currentMultiValue = yAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                      const favObject = {}
                      const segmentationObject = { xAttributesSegIndex, segmentation }
                      const currentMultiValueObject = { currentMultiValue }
                      const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                      groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                      yAxisSepList.push(axisSepData.axisSep)
                    }
                  })
                }
                else {
                  // no multi value
                  const favObject = {}
                  const segmentationObject = { yAxisAttributeSegIndex, segmentation }
                  const currentMultiValueObject = {}
                  const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                  groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                  yAxisSepList.push(axisSepData.axisSep)
                }
              })
            }
            else {
              if (multiValueYAttributes.length) {
                multiValueYAttributes.forEach((attribute) => {
                  for (let valueIndex = 0; valueIndex < yAxisMultiValueAttributeValuesMap[attribute].length; valueIndex++) {
                    const currentMultiValue = yAxisMultiValueAttributeValuesMap[attribute][valueIndex]
                    const favObject = {}
                    const segmentationObject = {}
                    const currentMultiValueObject = { currentMultiValue }
                    const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                    groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                    yAxisSepList.push(axisSepData.axisSep)
                  }
                })
              }
              else {
                // no multi value
                const favObject = {}
                const segmentationObject = {}
                const currentMultiValueObject = {}
                const axisSepData = this.getAxisSepValue(catalog, selectedYAttributes, article, favObject, segmentationObject, currentMultiValueObject, yAxisMultiValueAttributeValuesMap, retailPg, wholesalePg, outletPg)
                groups.get(articleGroupByAttributeValue).yAxisValues.push({ yAxisSep: axisSepData.axisSep, sortOrder: axisSepData.sortOrderForAxis.toLowerCase() })
                yAxisSepList.push(axisSepData.axisSep)
              }
            }
          }
        }

        const articleClone = clone(article)
        // Find max height of attributes of Articles
        const articleArticleHeight = await this.getArticleAttributesHeight(catalog, articleClone, articleAttributes, retailPg, wholesalePg, outletPg)

        articleClone._maxHeight = articleArticleHeight.maxHeight
        if (articleAttributes.length && fitToOnePage && this.fitInOnePageProps.articleAttributeMaxHeight < articleClone._maxHeight) {
          this.fitInOnePageProps.articleAttributeMaxHeight = articleClone._maxHeight
          this.fitInOnePageProps.articleMaxHeightText = articleArticleHeight ? articleArticleHeight.textForWidth : ''
        }
        articleClone._skuCountHeight = 0
        if (isGroupByModel && isShowSkuCount) {
          articleClone._skuCountHeight = this.getArticleSkuCountHeight(articleClone)
        }
        // check for sort order string
        if (sortAttributes.length !== 0) {
          articleClone._sortOrderString = this.generateSortOrderString(articleClone, sortAttributes, myAttributes).toLowerCase()
        }
        /// If xAxis or yAxis attribute has '_FavoriteTags', then might be possible for the multiple sep for 1 article , thas why irritating on all the sep and add in the map.
        for (let y = 0; y < yAxisSepList.length; y++) {
          for (let i = 0; i < xAxisSepList.length; i++) {
            if (groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.has(yAxisSepList[y])) {
              if (groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).xAxisGroup.has(xAxisSepList[i])) {
                if (sortAttributes.length !== 0) {
                  if (sortOrder === 'descending') {
                    utils.insertSorted(articleClone, groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).xAxisGroup.get(xAxisSepList[i]).articleInfo.articles, (a, b) =>
                      utils.comparer(b, a, ['_sortOrderString']))
                  }
                  else {
                    utils.insertSorted(articleClone, groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).xAxisGroup.get(xAxisSepList[i]).articleInfo.articles, (a, b) =>
                      utils.comparer(a, b, ['_sortOrderString']))
                  }
                }
                else {
                  groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).xAxisGroup.get(xAxisSepList[i]).articleInfo.articles.push(articleClone)
                  // if user setting showStyleCount or showSKUCount is enabled show style count and SKU count based on current article list conditions
                  // if (this.showInHeaderStyleCount) {
                  //   if (!groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).yAxisHeaderInfo._styles.includes(articleClone.ModelNumber)) {
                  //     groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).yAxisHeaderInfo._styles.push(articleClone.ModelNumber)
                  //   }
                  // }
                  // if (this.showInHeaderSkuCount) {
                  //   groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).yAxisHeaderInfo._skuCount++
                  // }
                }
              }
              else {
                const xHeaderGroup = {
                  articleInfo: {
                    articles: [articleClone],
                  },
                }
                groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.get(yAxisSepList[y]).xAxisGroup.set(xAxisSepList[i], xHeaderGroup)
              }
            }
            else {
              const titleWidth = utils.getTextWidth(yAxisSepList[y], `${this.headerProp.fontWeight} ${this.headerProp.fontSize}px ${this.font}`)
              const yHeaderGroup = {
                yAxisHeaderInfo: {
                  articleId: article.Id,
                  title: {
                    articleProperties: selectedYAttributes,
                    titleWidth,
                  },
                  _styles: [],
                  //  _skuCount will be the total number of articles
                  _skuCount: 0,
                },
                xAxisGroup: new Map(),
              }
              // if user setting showStyleCount is enabled show style count and SKU count based on current article list conditions
              // if (this.showInHeaderStyleCount) {
              //   if (!yHeaderGroup.yAxisHeaderInfo._styles.includes(articleClone.ModelNumber)) {
              //     yHeaderGroup.yAxisHeaderInfo._styles.push(articleClone.ModelNumber)
              //   }
              // }
              // if (this.showInHeaderSkuCount) {
              //   yHeaderGroup.yAxisHeaderInfo._skuCount++
              // }
              const xHeaderGroup = {
                articleInfo: {
                  articles: [articleClone],
                },
              }
              yHeaderGroup.xAxisGroup.set(xAxisSepList[i], xHeaderGroup)
              groups.get(articleGroupByAttributeValue).yAxisToxAxisMap.set(yAxisSepList[y], yHeaderGroup)
            }
          }
        }
      }
    }

    // find unique values for xAxis and y axis and sort them with sort order
    groups.forEach((value, key) => {
      const xAxisUniqueValuesMap = new Map()
      value.xAxisValues.forEach((xAxisValue) => {
        if (!xAxisUniqueValuesMap.has(xAxisValue.xAxisSep)) {
          xAxisUniqueValuesMap.set(xAxisValue.xAxisSep, xAxisValue)
        }
      })
      const yAxisUniqueValuesMap = new Map()
      value.yAxisValues.forEach((yAxisValue) => {
        if (!yAxisUniqueValuesMap.has(yAxisValue.yAxisSep)) {
          yAxisUniqueValuesMap.set(yAxisValue.yAxisSep, yAxisValue)
        }
      })
      if (fitToOnePage) {
        value.yAxisToxAxisMap.forEach((groupData) => {
          groupData.xAxisGroup.forEach((xAxisValue) => {
            this.fitInOnePageProps.maximumArticleInRow = this.fitInOnePageProps.maximumArticleInRow < xAxisValue.articleInfo.articles.length ? xAxisValue.articleInfo.articles.length : this.fitInOnePageProps.maximumArticleInRow
          })
        })
      }
      this.xAxisUniqueValues[key] = []
      this.yAxisUniqueValues[key] = []
      this.xAxisUniqueValues[key] = utils.sort(Array.from(xAxisUniqueValuesMap.values()), 'sortOrder')
      this.yAxisUniqueValues[key] = utils.sort(Array.from(yAxisUniqueValuesMap.values()), 'sortOrder')
      this.fitInOnePageProps.maximumXAxisUniqueValues = this.fitInOnePageProps.maximumXAxisUniqueValues < this.xAxisUniqueValues[key].length ? this.xAxisUniqueValues[key].length : this.fitInOnePageProps.maximumXAxisUniqueValues
      this.fitInOnePageProps.maximumYAxisUniqueValues = this.fitInOnePageProps.maximumYAxisUniqueValues < this.yAxisUniqueValues[key].length ? this.yAxisUniqueValues[key].length : this.fitInOnePageProps.maximumYAxisUniqueValues
    })
    return groups
  }

  getArticleSkuCountHeight(article) {
    if (utils.isDefined(article._skuCount)) {
      const skuCountText = `SKU Count - ${article._skuCount}\n`
      return this.getTextBoxHeight(skuCountText, this.articleAttributeProp.fontSize, this.font, this.articleImageProp.imgSize)
    }
  }

  getTextBoxHeight(text: string, fontSize: number, font: string, width: number) {
    const textBox = new MbTextBox(text, {
      top: 0,
      left: 0,
      width,
      fontFamily: font,
      fontSize,
    })
    return textBox.height
  }

  getArticleAttributesHeight(catalog: CatalogDetails, article: MyArticle, articleAttributes: string[], retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup) {
    let textForWidth = ''
    for (let attributeIndex = 0; attributeIndex < articleAttributes.length; attributeIndex++) {
      const attributeLabel = this.mapOfSelectedArticleAttributesSystemNameToDisplayLabel.get(articleAttributes[attributeIndex])
      const emptyValuePlaceHolder = '[Blank]'
      if (utils.isDefined(attributeLabel)) {
        textForWidth += `${attributeLabel}: `
      }
      const valueAtPath = article[articleAttributes[attributeIndex]]
      if (utils.isDefined(valueAtPath)) {
        if (articleAttributes[attributeIndex].toLowerCase() === '_retailprice') {
          textForWidth += utils.isDefined(valueAtPath) ? utils.formatPrice(retailPg, Number(valueAtPath), catalog?.Config.ShowPriceThousandsSeparated) : emptyValuePlaceHolder
        }
        else if (articleAttributes[attributeIndex].toLowerCase() === '_wholesaleprice') {
          textForWidth += utils.isDefined(valueAtPath) ? utils.formatPrice(wholesalePg, Number(valueAtPath), catalog?.Config.ShowPriceThousandsSeparated) : emptyValuePlaceHolder
        }
        else if (articleAttributes[attributeIndex].toLowerCase() === '_outletprice') {
          textForWidth += utils.isDefined(valueAtPath) ? utils.formatPrice(outletPg, Number(valueAtPath), catalog?.Config.ShowPriceThousandsSeparated) : emptyValuePlaceHolder
        }
        else if (articleAttributes[attributeIndex].toLowerCase() === 'colorid') {
          textForWidth += utils.isDefined(valueAtPath) ? article.ColorName ? article.ColorName : valueAtPath : emptyValuePlaceHolder
        }
        else {
          textForWidth += article[articleAttributes[attributeIndex]]
        }
      }
      if (attributeIndex !== (articleAttributes.length - 1)) {
        textForWidth += '\n'
      }
    }
    return { textForWidth, maxHeight: this.getTextBoxHeight(textForWidth, this.articleAttributeProp.fontSize, this.font, this.articleImageProp.imgSize) }
  }

  findFontSizeDependingOnTheHeight(scaling, fontSize, maxHeight) {
    const textBoxHeight = this.getTextBoxHeight(this.fitInOnePageProps.articleMaxHeightText, fontSize, this.font, this.articleImageProp.imgSize)
    if (textBoxHeight && maxHeight >= textBoxHeight) {
      this.fitInOnePageProps.articleAttributeMaxHeight = textBoxHeight
      return fontSize
    }
    else {
      fontSize = fontSize - 1
      return this.findFontSizeDependingOnTheHeight(scaling, fontSize, maxHeight)
    }
  }

  changePropsAsPerImageScaleFactor(imageScaleFactor: number) {
    if (utils.isDefined(imageScaleFactor)) {
      this.articleImageProp.imageDefaultScaleFactor = imageScaleFactor
      this.articleImageProp.imgSize = this.articleImageProp.imageWidth * imageScaleFactor
      if (imageScaleFactor === merchConstants.slideImageDefaultScaleFactors.small) {
        this.articleAttributeProp.fontSize = merchConstants.slideArticleThumbnailFontSizes.small
      }
      else if (imageScaleFactor === merchConstants.slideImageDefaultScaleFactors.medium) {
        this.articleAttributeProp.fontSize = merchConstants.slideArticleThumbnailFontSizes.medium
      }
      else {
        this.articleAttributeProp.fontSize = merchConstants.slideArticleThumbnailFontSizes.large
      }
    }
  }

  findNumberOfArticlesInEachColumn(numberOfColumns: number) {
    // default minimum horizontal padding between articles
    this.articleImageProp.imgHorSpacing = 9
    const widthOfEachColumn = (this.slideWidth - this.headerHeight) / numberOfColumns
    const numberOfArticlesInEachColumn = Math.floor(widthOfEachColumn / (this.articleImageProp.imgSize + this.articleImageProp.imgHorSpacing))
    if (numberOfArticlesInEachColumn !== 0) {
      const totalSpacingLeft = widthOfEachColumn - (this.articleImageProp.imgSize * numberOfArticlesInEachColumn)
      this.articleImageProp.imgHorSpacing = totalSpacingLeft / (numberOfArticlesInEachColumn + 1)
    }
    return numberOfArticlesInEachColumn
  }

  findAndSetShowLabelMerchAttributes(catalog: CatalogDetails, myAttributes: IMyAttribute[]) {
    this.merchLabelAttributes = []
    // check for config MerchLabelAttributes and find the attributes with which need to show the labels
    const configMerchLabelAttributes = catalog?.Config.MerchLabelAttributes
    if (!utils.isDefined(configMerchLabelAttributes)) {
      this.merchLabelAttributes = myAttributes
    }
    else if (utils.isDefined(configMerchLabelAttributes) && configMerchLabelAttributes.length === 0) {
      this.merchLabelAttributes = []
    }
    else {
      for (let i = 0; i < myAttributes.length; i++) {
        if (configMerchLabelAttributes.includes(myAttributes[i].SystemName)) {
          this.merchLabelAttributes.push(myAttributes[i])
        }
      }
    }
  }

  changePropsAsPerSlideSize(slideSize: string, numberOfColumns: number) {
    this.slideHeight = merchConstants.slideSizeCanvasDimensions[slideSize].height
    this.slideWidth = merchConstants.slideSizeCanvasDimensions[slideSize].width
    this.remainingHeight = this.slideHeight - this.yAxisProp.rowTop
    this.xAxisProp.columnWidth = (this.slideWidth - this.xAxisProp.leftMargin) / numberOfColumns
  }

  findTheScalingFactor(totalCards, scaleX, scaleY, width, height) {
    const eachCardWidth = this.articleImageProp.imgSize
    const eachCardHeight = this.fitInOnePageProps.articleAttributeMaxHeight + this.articleImageProp.imgSize
    this.fitInOnePageProps.scaleX = scaleX - 0.02
    this.fitInOnePageProps.scaleY = scaleY - 0.02

    const newWidth = eachCardWidth * this.fitInOnePageProps.scaleX
    const hewHeight = eachCardHeight * this.fitInOnePageProps.scaleY
    const maximumNumberOCardsInOneRow = Math.floor(width / newWidth)
    const maximumNumberOfCardsInOneColumn = Math.floor((height) / hewHeight)
    const totalCardsCanAccommodateInOnePage = maximumNumberOCardsInOneRow * maximumNumberOfCardsInOneColumn
    if (totalCardsCanAccommodateInOnePage >= totalCards || scaleX <= 0.4) {
      if (scaleX < 0.4) {
        this.needOverLapping = true
        this.fitInOnePageProps.scaleX = 0.4
        this.fitInOnePageProps.scaleY = 0.4
      }
    }
    else {
      this.findTheScalingFactor(totalCards, this.fitInOnePageProps.scaleX, this.fitInOnePageProps.scaleY, width, height)
    }
  }

  findNeedsOverLapping(width: number, height: number, numberOfCards: number) {
    const overlappingObject: IOverlapingObject = {
      canAccommodate: false,
      canAccommodateWithoutSpacing: false,
      overlappingWidthPercentage: 0,
      maxNumberOfRowsCanBe: 0,
      overlappingHeight: 0,
      maxNumberOfCardsCanBeInOneRow: 0,
    }
    const articleMaxHeight = this.fitInOnePageProps.articleAttributeMaxHeight + this.articleImageProp.imgSize
    const numberOfROws = Math.floor(height / articleMaxHeight)
    const numbersOfCardsInEachROw = Math.floor(width / (this.articleImageProp.imgSize + this.articleImageProp.imgHorSpacing))
    const numbersOfCardsInEachROwWithoutSpacing = Math.floor(width / this.articleImageProp.imgSize)
    if (numberOfROws * numbersOfCardsInEachROw >= numberOfCards) {
      overlappingObject.canAccommodate = true
    }
    else if (numberOfROws * numbersOfCardsInEachROwWithoutSpacing >= numberOfCards) {
      overlappingObject.canAccommodateWithoutSpacing = true
    }
    else {
      // find the overlapping
      overlappingObject.maxNumberOfRowsCanBe = Math.round(height / articleMaxHeight)
      overlappingObject.maxNumberOfCardsCanBeInOneRow = Math.ceil(numberOfCards / overlappingObject.maxNumberOfRowsCanBe)
      overlappingObject.overlappingWidthPercentage = this.articleImageProp.imgSize - ((width - this.articleImageProp.imgSize) / (overlappingObject.maxNumberOfCardsCanBeInOneRow <= 1 ? 1 : overlappingObject.maxNumberOfCardsCanBeInOneRow - 1))
    }
    return overlappingObject
  }

  drawPivotFitToOnePageArticles(currentObjects, articles, columnNumber, imageType, eachCellWidth, eachCellHeight, props, options) {
    let overLappingWidthFactor = 0
    let totalArticlesCanAccommodateInOneRow = this.fitInOnePageProps.maximumArticleInRow > this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant ? this.fitInOnePageProps.maximumArticlesToConsiderForOneQuadrant : this.fitInOnePageProps.maximumArticleInRow
    const overlappingObject = this.findNeedsOverLapping(eachCellWidth, eachCellHeight, articles.length)
    if (overlappingObject.canAccommodate) {
      totalArticlesCanAccommodateInOneRow = Math.floor(eachCellWidth / (this.articleImageProp.imgSize + this.articleImageProp.imgHorSpacing))
    }
    else if (overlappingObject.canAccommodateWithoutSpacing) {
      totalArticlesCanAccommodateInOneRow = Math.floor(eachCellWidth / this.articleImageProp.imgSize)
    }
    else {
      // needs overLapping
      overLappingWidthFactor = overlappingObject.overlappingWidthPercentage
      totalArticlesCanAccommodateInOneRow = overlappingObject.maxNumberOfCardsCanBeInOneRow
    }
    let top = this.yAxisProp.rowTop
    let left = this.xAxisProp.columnWidth * (columnNumber)
    articles.forEach((article, index) => {
      if (index === 0) {
        // (overlappingObject.canAccommodate ? this.articleImageProp.imgVerSpacing : 0) +
        top += this.articleImageProp.imgSize / 2
      }
      if (index % totalArticlesCanAccommodateInOneRow === 0 && index !== 0) {
        top += this.articleImageProp.imgSize + this.fitInOnePageProps.articleAttributeMaxHeight
      }
      if (index !== 0 && index % totalArticlesCanAccommodateInOneRow !== 0) {
        left = left - overLappingWidthFactor
      }
      if (index === 0 || index % totalArticlesCanAccommodateInOneRow === 0) {
        left = this.xAxisProp.columnWidth * (columnNumber)
      }
      left += this.articleImageProp.imgSize / 2
      currentObjects.push(this.createImage(article.Id, left, top, imageType, this.articleImageProp.imgSize / this.articleImageProp.imageWidth, this.articleImageProp.imgSize / this.articleImageProp.imageHeight))
      if (options.displayAttributes.length) {
        currentObjects.push(...this.createArticleAttributes(article, props, top, left - this.articleImageProp.imgSize / 2, options.groupByModel, options.showSkuCount))
      }
      left += this.articleImageProp.imgSize / 2 + (overlappingObject.canAccommodate ? this.articleImageProp.imgHorSpacing : 0)
    })
  }

  createArticleImagesWithAttribute(currentObjects, articles, columnNumber, imageType) {
    articles.forEach((article, index) => {
      const top = this.yAxisProp.rowTop + this.articleImageProp.imgVerSpacing + this.articleImageProp.imgSize / 2
      const left = (this.xAxisProp.columnWidth * (columnNumber))
        + (index * (this.articleImageProp.imgSize + this.articleImageProp.imgHorSpacing) + this.articleImageProp.imgSize / 2 + this.articleImageProp.imgHorSpacing)
      currentObjects.push(this.createImage(article.Id, left, top, imageType, this.articleImageProp.imageDefaultScaleFactor, this.articleImageProp.imageDefaultScaleFactor))
      this.articleAttributesForCurrentPage.push({ article, top, left: left - this.articleImageProp.imgSize / 2 })
    })
  }

  createArticleAttributes(article, props, top, left, isGroupByModel, isShowBySkuCount) {
    const attributeObjects: any[] = []
    let topValue = top + this.articleImageProp.imgSize / 2
    // if isGroupByModel is true add sku count value
    if (isGroupByModel && isShowBySkuCount) {
      // topValue +=  article._skuCountHeight / 2
      const skuCountText = `SKU Count - ${article._skuCount}`
      const skuCountAttribute = this.createTextObject(
        skuCountText,
        topValue,
        left,
        this.articleImageProp.imgSize,
        article._skuCountHeight,
        this.articleAttributeProp.fontSize,
        'normal',
        'center',
        '#757575',
      )
      attributeObjects.push(skuCountAttribute)
      topValue += article._skuCountHeight
    }
    if (props.articleAttributes.length !== 0) {
      const eachAttributeObjectObject = this.createLabel(
        article.Id,
        props.articleAttributes,
        '\n',
        topValue,
        left,
        this.articleImageProp.imgSize,
        article._maxHeight,
        this.articleAttributeProp.fontSize,
        'normal',
        'center',
        '#757575',
        '#FFFFFF',
        false,
        false,
      )
      attributeObjects.push(eachAttributeObjectObject)
      topValue += article._maxHeight / 2
    }
    return attributeObjects
  }

  createTextObject(text, top, left, width, height, fontSize, fontWeight, textAlign, color) {
    text = text != null ? text.toString() : ''
    return {
      type: 'textbox',
      top,
      left,
      width,
      height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text,
      fill: color,
      backgroundColor: '',
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      locked: false,
      isOriginTopLeft: true,
    }
  }

  createLabel(
    articleId,
    articleProps,
    articlePropsSeparator,
    top,
    left,
    width,
    height,
    fontSize,
    fontWeight,
    textAlign,
    color,
    backgroundColor,
    showLabels = false,
    templateObject = true, // By default all article details object are template object
  ) {
    if (!Array.isArray(articleProps)) {
      articleProps = [articleProps]
    }
    return {
      type: 'articleDetails',
      articleId,
      top,
      left,
      angle: 0,
      width,
      height,
      scaleX: 1,
      scaleY: 1,
      fill: color,
      backgroundColor,
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps,
        separator: articlePropsSeparator,
      },
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      locked: false,
      showLabels,
      isOriginTopLeft: true,
      templateObject,
    }
  }

  getColumnsForPage(xAxisUniqueValues, numberOfColumns) {
    if (xAxisUniqueValues.length > 0) {
      if (xAxisUniqueValues.length > this.xAxisProp.columnCounter) {
        return xAxisUniqueValues.slice(this.xAxisProp.columnCounter, this.xAxisProp.columnCounter + numberOfColumns)
      }
    }
  }

  onNewPage(myAttributes, currentObjects, currentColumns, selectedXAttributes, titleOnEachSlide, slideTitle, showSlideTitleCounter, props) {
    props.pageNumber++
    currentObjects.length = 0
    this.yAxisProp.rowTop = 0
    this.previousQuadrantEnd = 0
    if (this.headerLogoData) {
      currentObjects.push(this.getHeaderLogo())
    }
    this.yAxisProp.rowTop += this.logoHeight * this.logoScaling
    if (titleOnEachSlide) {
      let title = slideTitle
      if (showSlideTitleCounter && props.pageNumber !== 1) {
        title += ` - ${props.pageNumber}`
      }
      currentObjects.push(this.createTitleObj(title))
    }

    if (this.displayHorizontalAxis) {
      for (const column in currentColumns) {
        currentObjects.push(...this.createXAxisColumnLabelObjectToHeader(myAttributes, selectedXAttributes, currentColumns[column], column))
      }
      this.yAxisProp.rowTop += this.headerHeight
    }

    this.remainingHeight = this.slideHeight - this.yAxisProp.rowTop
    this.previousRowEnd = this.yAxisProp.rowTop
    this.previousQuadrantEnd = this.yAxisProp.rowTop
  }

  createXAxisColumnLabelObjectToHeader(myAttributes, xAttributes, currentColumn, column) {
    // As discussed with sohail, In case of XAxis Attribute has '_FavoriteTags', then instead of article details object we display the textbox
    // because the article details object is unaware of which tag to display if article has multiple.
    // Also , we are checking for the '_FavoriteTags' exists in  the attribute list as might be possible user select multiple attributes
    // and if this exists and we use the article details object then , fav tag property will be missed.
    const multiValueAttributes = xAttributes.filter(attribute => myAttributes[attribute] && myAttributes[attribute].AttributeType === 'multivalue')
    if (!xAttributes.includes('_FavoriteTags') && !xAttributes.includes('_Segmentation') && multiValueAttributes.length === 0) {
      const headerTitle = {
        type: 'articleDetails',
        articleId: currentColumn.articleId,
        top: this.yAxisProp.rowTop + 10,
        left: (this.xAxisProp.columnWidth * column) + (column === 0 ? this.xAxisProp.leftMargin : 0),
        angle: 0,
        width: this.xAxisProp.columnWidth,
        height: this.labelHeightPerLine,
        scaleX: 1,
        scaleY: 1,
        fill: '#000000',
        backgroundColor: '',
        flipX: false,
        flipY: false,
        customOptions: {
          articleProps: xAttributes,
          separator: ' / ',
          attributePlaceHolder: '[Blank]',
        },
        fontFamily: this.font,
        fontSize: this.headerProp.fontSize,
        fontStyle: this.headerProp.fontStyle,
        fontWeight: this.headerProp.fontWeight,
        textAlign: 'left',
        locked: false,
        showLabel: false,
        isOriginTopLeft: true,
        templateObject: true,
      }
      return [headerTitle]
    }
    else {
      const text = currentColumn.xAxisSep
      const headerTitle = {
        type: 'textbox',
        top: this.yAxisProp.rowTop + 10,
        left: (this.xAxisProp.columnWidth * column) + (column === 0 ? this.xAxisProp.leftMargin : 0),
        angle: 0,
        width: this.xAxisProp.columnWidth,
        height: this.labelHeightPerLine,
        scaleX: 1,
        scaleY: 1,
        fill: '#000000',
        backgroundColor: '',
        flipX: false,
        flipY: false,
        text,
        fontFamily: this.font,
        fontSize: this.headerProp.fontSize,
        fontStyle: this.headerProp.fontStyle,
        fontWeight: this.headerProp.fontWeight,
        textAlign: 'left',
        locked: false,
        showLabel: false,
        isOriginTopLeft: true,
      }
      return [headerTitle]
    }
  }

  createYAxisColumnLabelObjectToHeader(headerInfo, yAxisSepObject, selectedYAttributes, myAttributes) {
    // As discussed with sohail, In case of YAxis Attribute has '_FavoriteTags', then instead of article details object we display the textbox
    // because the article details object is unaware of which tag to display if article has multiple.
    // Also , we are checking for the '_FavoriteTags' exists in  the attribute list as might be possible user select multiple attributes
    // and if this exists and we use the article details object then , fav tag property will be missed.
    const multiValueAttributes = selectedYAttributes.filter(attribute => myAttributes[attribute] && myAttributes[attribute].AttributeType === AttributeType.MultiValue)
    const headerObjects: any[] = []
    const headerTitleWidthObject = {
      styleTitleWidth: 0,
      skuTitleWidth: 0,
    }
    let leftPosition = 10
    if (selectedYAttributes.length) {
      if (!selectedYAttributes.includes('_FavoriteTags') && !selectedYAttributes.includes('_Segmentation') && multiValueAttributes.length === 0) {
        const headerTitle = {
          type: 'articleDetails',
          articleId: headerInfo.articleId,
          top: ((this.yAxisProp.rowTop + this.headerHeight / 2)),
          left: leftPosition,
          angle: 0,
          width: this.slideWidth,
          height: this.headerHeight,
          scaleX: 1,
          scaleY: 1,
          fill: '#000000',
          backgroundColor: '',
          flipX: false,
          flipY: false,
          customOptions: {
            articleProps: headerInfo.title.articleProperties,
            separator: ' / ',
            attributePlaceHolder: '[Blank]',
          },
          fontFamily: this.font,
          fontSize: this.headerProp.fontSize,
          fontStyle: this.headerProp.fontStyle,
          fontWeight: this.headerProp.fontWeight,
          textAlign: 'left',
          locked: false,
          showLabel: false,
          isOriginTopLeft: true,
          templateObject: true,
        }
        headerObjects.push(headerTitle)
      }
      else {
        const text = yAxisSepObject.yAxisSep
        const headerTitleWidth = utils.getTextWidth(text, `${this.headerProp.fontWeight} ${this.headerProp.fontSize}px ${this.font}`)
        const headerTitle = {
          type: 'textbox',
          top: ((this.yAxisProp.rowTop + this.headerHeight / 2)),
          left: leftPosition,
          angle: 0,
          width: headerTitleWidth,
          height: this.headerHeight,
          scaleX: 1,
          scaleY: 1,
          fill: '#000000',
          backgroundColor: '',
          flipX: false,
          flipY: false,
          text,
          fontFamily: this.font,
          fontSize: this.headerProp.fontSize,
          fontStyle: this.headerProp.fontStyle,
          fontWeight: this.headerProp.fontWeight,
          textAlign: 'left',
          locked: false,
          showLabel: false,
          isOriginTopLeft: true,
        }
        headerObjects.push(headerTitle)
      }
      leftPosition += headerInfo.title.titleWidth + this.headerProp.paddingBetweenTitleLabels
    }

    // if user setting showStyleCount is enabled
    // if (this.showInHeaderStyleCount) {
    //   const styleTitleWidth = utils.getTextWidth(`${headerInfo._styles.length} Styles`, `${this.headerProp.fontWeight} ${this.headerProp.fontSize}px ${this.font}`, canvas)
    //   headerTitleWidthObject.styleTitleWidth = styleTitleWidth
    //   const styleTitle = this.createTextObject(
    //     `${headerInfo._styles.length} Styles`, // text
    //     ((this.yAxisProp.rowTop + this.headerHeight / 2)), // top
    //     leftPosition, // left
    //     styleTitleWidth, // width
    //     this.headerHeight, // height
    //     this.headerProp.fontSize, // fontSize
    //     this.headerProp.fontWeight, // fontWeight
    //     'left', // textAlign
    //     '#000000', // color
    //   )
    //   headerObjects.push(styleTitle)
    // }
    leftPosition += headerTitleWidthObject.styleTitleWidth + this.headerProp.paddingBetweenTitleLabels
    // if showSKUCount is true then we will show the total articles counts labeled as Articles
    // if (this.showInHeaderSkuCount) {
    //   const titleLabel = ' Articles'
    //   const skuTitleWidth = utils.getTextWidth(headerInfo._skuCount + titleLabel, `${this.headerProp.fontWeight} ${this.headerProp.fontSize}px ${this.font}`, canvas)
    //   headerTitleWidthObject.skuTitleWidth = skuTitleWidth
    //   const skuTitle = this.createTextObject(
    //     headerInfo._skuCount + titleLabel, // text
    //     ((this.yAxisProp.rowTop + this.headerHeight / 2)), // top
    //     leftPosition, // left
    //     skuTitleWidth, // width
    //     this.headerHeight, // height
    //     this.headerProp.fontSize, // fontSize
    //     this.headerProp.fontWeight, // fontWeight
    //     'left', // textAlign
    //     '#000000', // color
    //   )
    //   headerObjects.push(skuTitle)
    // }
    return headerObjects
  }

  createImage(articleId, left, top, imageType, scaleX, scaleY) {
    return {
      type: 'articleImage',
      top,
      left,
      width: this.articleImageProp.imageWidth,
      height: this.articleImageProp.imageHeight,
      articleId,
      angle: 0,
      scaleX,
      scaleY,
      flipX: false,
      flipY: false,
      locked: false,
      shouldAllowTemplateBigImagesToResize: true,
      imageType,
    }
  }

  createTitleObj(title) {
    title = title != null ? title.toString() : ''
    return {
      type: 'textbox',
      top: 40,
      left: this.logoWidth + 10,
      width: this.slideWidth - this.logoWidth - 10,
      height: this.slideTitleProps.height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text: title,
      fill: '#000000',
      backgroundColor: '',
      fontFamily: this.font,
      fontSize: this.slideTitleProps.fontSize,
      fontStyle: 'normal',
      fontWeight: 'bold',
      textAlign: 'left',
      locked: false,
      isOriginTopLeft: true,
      templateObject: true,
    }
  }

  async getHeaderLogoData(catalogCode: number) {
    const resources = await appConfig.DB!.resources.where({ CatalogCode: catalogCode }).filter(resource => resource.ResourceType === 'T1S-TemplateLogo' && resource.ResourceName === 'visuallinebuilderheader' && resource.Status === 1).toArray()
    return new Promise((resolve) => {
      if (resources.length) {
        axios.get(`${resources[0].ResourceUrl}`, { responseType: 'arraybuffer' }).then((res) => {
          const base64 = res.data ? `data:image/png;base64,${utils.arrayBufferToBase64(res.data)}` : null
          if (base64) {
            const settings = {
              width: this.logoWidth,
              height: this.logoHeight,
            }
            const image = new Image()
            image.onload = function () {
              resolve(utils.resizeImageAndGetBase64(image, settings) as string)
            }
            image.src = (base64 as string)
          }
          else {
            resolve(this.defaultHeaderLogoData)
          }
        }).catch((err) => {
          console.log(err)
          resolve(this.defaultHeaderLogoData)
        })
      }
      else {
        resolve(this.defaultHeaderLogoData)
      }
    })
  }

  getHeaderLogo() {
    return {
      type: 'image',
      top: 65,
      left: 70,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 0,
      scaleY: this.logoScaling,
      scaleX: this.logoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.headerLogoData,
    }
  }

  createSlide(merch, objects: any[], titleOnEachSlide: boolean, props, slideTitle: string, options: Record<string, any>, articleIdList) {
    props.slideNumber++
    let newSlideName = `slide - ${padStart(props.slideNumber.toString(), 3, '0')}`

    if (titleOnEachSlide) {
      newSlideName = props.pageNumber !== 1 ? `${slideTitle} - ${props.pageNumber}` : slideTitle
    }
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === newSlideName))) {
      let c = 0
      do {
        c++
        newSlideName = `${newSlideName} (${c})`
      } while (folderNode.children.find(node => node.label === newSlideName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, newSlideName, objects, options.slideSize, options.imageScaleFactor, sortOrder, true, articleIdList)
  }

  // TODO: I think default values will impact the sorting
  getSortOrder(attributeSystemName, articleAttributeValue) {
    const lowerCaseArticleAttributeValue = articleAttributeValue != null ? articleAttributeValue.toString().toLowerCase().trim() : articleAttributeValue
    if (attributeSystemName === 'Category') {
      switch (lowerCaseArticleAttributeValue) {
        case 'men\'s training':
          return '001'
        case 'women\'s training':
          return '002'
        case 'run':
          return '003'
        case 'youth performance':
          return '004'
        case 'sportstyle':
          return '005'
        case 'basketball':
          return '006'
        case 'team sports':
          return '007'
        case 'global football':
          return '008'
        case 'golf':
          return '009'
        case 'outdoor':
          return '010'
        default :
          return lowerCaseArticleAttributeValue
      }
    }
    else if (attributeSystemName === 'UADivision') {
      switch (lowerCaseArticleAttributeValue) {
        case 'apparel':
          return '001'
        case 'footwear':
          return '002'
        case 'accessories':
          return '003'
        default:
          return lowerCaseArticleAttributeValue
      }
    }
    else if (attributeSystemName === 'EndUse') {
      switch (lowerCaseArticleAttributeValue) {
        case 'train':
          return '001'
        case 'run':
          return '002'
        case 'lifestyle':
          return '003'
        case 'basketball':
          return '004'
        case 'football':
          return '005'
        case 'lacrosse':
          return '006'
        case 'track':
          return '007'
        case 'baseball':
          return '008'
        case 'volleyball':
          return '009'
        case 'tennis':
          return '010'
        case 'softball':
          return '011'
        case 'rugby':
          return '012'
        case 'hockey':
          return '013'
        case 'field hockey':
          return '014'
        case 'sideline':
          return '015'
        case 'global football':
          return '016'
        case 'golf':
          return '017'
        case 'hunt':
          return '018'
        case 'fish':
          return '019'
        case 'military/tactical':
          return '020'
        case 'hike':
          return '021'
        case 'mountain':
          return '022'
        default :
          return lowerCaseArticleAttributeValue
      }
    }
    else if (attributeSystemName === 'SubSubSilhouette') {
      switch (lowerCaseArticleAttributeValue) {
        case 'crew neck':
          return '001'
        case 'v-neck':
          return '002'
        case 'mock neck':
          return '003'
        case 'mock':
          return '004'
        case 'scoop neck':
          return '005'
        case 'henley':
          return '006'
        case 'pull-over hoodie':
          return '007'
        case '1/4 zip':
          return '008'
        case 'polo':
          return '009'
        case 'button-down':
          return '010'
        case '7 in. to 10 in.':
          return '011'
        case 'n/a':
          return '012'
        case 'popover':
          return '013'
        case 'funnel neck':
          return '014'
        case '1/2 zip':
          return '015'
        case 'full-zip':
          return '016'
        case 'pinnie':
          return '017'
        case 'racerback':
          return '018'
        case 'halter':
          return '019'
        case 'camisole':
          return '020'
        case 'pull-over':
          return '021'
        case 'fixed insulation':
          return '022'
        case 'cardigan':
          return '023'
        case 'traditional':
          return '024'
        case '10.5 in. or above':
          return '025'
        case '4 in. to 6.5 in.':
          return '026'
        case '3.5 in. or less':
          return '027'
        case 'tapered leg':
          return '028'
        case 'straight leg':
          return '029'
        case 'jogger':
          return '030'
        case '3 pad':
          return '031'
        case '5 pad':
          return '032'
        case 'flare leg':
          return '033'
        case 'boot cut':
          return '034'
        case 'wide leg':
          return '035'
        case 'bikini':
          return '036'
        case 'shell':
          return '037'
        case 'soft shell':
          return '038'
        case 'windbreaker shell':
          return '039'
        case '3-in-1':
          return '040'
        case 'anorak':
          return '041'
        case 'parka':
          return '042'
        case 'insulated':
          return '043'
        case 'top and bottom':
          return '044'
        case 'low impact':
          return '045'
        case 'mid impact':
          return '046'
        case 'hi impact':
          return '047'
        case 'boxer jock':
          return '048'
        case 'boxer':
          return '049'
        case 'hipster':
          return '050'
        case 'thong':
          return '051'
        case 'low top':
          return '052'
        case 'mid top':
          return '053'
        case 'high top':
          return '054'
        case '5/8th':
          return '055'
        case 'velcro':
          return '056'
        case 'molded rubber':
          return '057'
        case 'flat brim':
          return '058'
        case 'curved brim':
          return '059'
        case 'bucket':
          return '060'
        case 'military':
          return '061'
        case 'visor':
          return '062'
        case 'classic':
          return '063'
        case 'skull':
          return '064'
        case 'full':
          return '065'
        case 'neck gaiter':
          return '066'
        case 'no show':
          return '067'
        case 'quarter':
          return '068'
        case 'low cut':
          return '069'
        case 'non-seamless toe':
          return '070'
        case 'dress':
          return '071'
        case 'over the calf':
          return '072'
        case 'knee':
          return '073'
        case 'seamless toe':
          return '074'
        case 'non-insulated':
          return '075'
        case 'half finger glove':
          return '076'
        case 'mitten':
          return '077'
        case 'traditional backpack':
          return '078'
        case 'sport backpack':
          return '079'
        case 'hydration':
          return '080'
        case 'sackpack':
          return '081'
        case 'traditional duffel':
          return '082'
        case 'sport duffel':
          return '083'
        case 'sling':
          return '084'
        case 'tote':
          return '085'
        case 'messenger':
          return '086'
        case 'case':
          return '087'
        case 'toiletry':
          return '088'
        case 'fanny Pack':
          return '089'
        case 'full Size':
          return '090'
        case 'carry-On':
          return '091'
        case 'calf':
          return '092'
        case 'elbow':
          return '093'
        case 'arm':
          return '094'
        case 'forearm':
          return '095'
        case 'towel':
          return '096'
        case 'scarf':
          return '097'
        case 'lanyard':
          return '098'
        case 'hand warmer':
          return '099'
        case 'umbrella':
          return '100'
        case '6 inch':
          return '101'
        case '5 inch':
          return '102'
        case '3 inch':
          return '103'
        case '1 inch':
          return '104'
        case '1/2 inch':
          return '105'
        case 'scale':
          return '106'
        case 'fitness system':
          return '107'
        case 'armband':
          return '108'
        case 'belt':
          return '109'
        case 'ball':
          return '110'
        case 'shin guard':
          return '111'
        default:
          return lowerCaseArticleAttributeValue
      }
    }
    else {
      return lowerCaseArticleAttributeValue
    }
  }
}
interface IOverlapingObject {
  canAccommodate: boolean
  canAccommodateWithoutSpacing: boolean
  overlappingWidthPercentage: number
  maxNumberOfRowsCanBe: number
  overlappingHeight: number
  maxNumberOfCardsCanBeInOneRow: number
}
export default new VisualLineBuilder()
